import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Calendar,
    Fuel,
    Gauge,
    Settings2,
    Zap,
    Palette,
    Car as CarIcon,
    ArrowLeft,
    Phone,
    Mail,
    ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { FORMAT, FUEL_BG, TRANS_BG, BODY_BG, SITE } from "@/lib/site";
import useDocumentMeta from "@/lib/useDocumentMeta";

export default function CarDetailPage() {
    const { id } = useParams();
    const [car, setCar] = useState(null);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setCar(null)).finally(() => setLoading(false));
    }, [id]);

    useDocumentMeta({
        title: car
            ? `${car.brand} ${car.model} ${car.year || ""} — ${FORMAT.money(car.price)} | Konstantin Mobile`
            : "Автомобил · Konstantin Mobile",
        description: car
            ? `${car.brand} ${car.model}${car.year ? ", " + car.year : ""}${car.mileage ? ", " + FORMAT.km(car.mileage) : ""}${car.fuel ? ", " + (FUEL_BG[car.fuel] || car.fuel) : ""}. Цена: ${FORMAT.money(car.price)}. Запазете час за оглед в Konstantin Mobile, Петрич.`
            : "Детайли за автомобила.",
        path: `/cars/${id}`,
        image: car?.images?.[0],
    });

    if (loading) return <div className="min-h-screen pt-32 px-8 text-ink-muted">Зареждане…</div>;
    if (!car) return (
        <div className="min-h-screen pt-32 px-8">
            <p className="text-ink-muted">Автомобилът не е намерен.</p>
            <Link to="/cars" className="text-gold mt-4 inline-block">← Към инвентара</Link>
        </div>
    );

    const specs = [
        { icon: Calendar, label: "Година", value: car.year },
        { icon: Gauge, label: "Пробег", value: FORMAT.km(car.mileage) },
        { icon: Fuel, label: "Гориво", value: FUEL_BG[car.fuel] || car.fuel },
        { icon: Settings2, label: "Скоростна кутия", value: TRANS_BG[car.transmission] || car.transmission },
        { icon: Zap, label: "Мощност", value: FORMAT.hp(car.horsepower) },
        { icon: CarIcon, label: "Каросерия", value: BODY_BG[car.body_style] || car.body_style },
        { icon: Palette, label: "Цвят", value: car.color || "—" },
    ];

    const images = car.images?.length ? car.images : ["https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?auto=format&fit=crop&w=1600&q=80"];

    return (
        <main data-testid="car-detail-page" className="relative pt-32 pb-24 min-h-screen bg-bg overflow-hidden">
            {/* KS logo watermark */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 flex items-start justify-center opacity-[0.08] select-none -z-0"
                style={{
                    backgroundImage: "url(/ks-logo.png)",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center 30%",
                    backgroundSize: "min(80vw, 1100px) auto",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <Link to="/cars" className="inline-flex items-center gap-2 text-ink-muted hover:text-gold mb-8 text-sm">
                    <ArrowLeft size={14} /> Към инвентара
                </Link>

                <div className="grid lg:grid-cols-12 gap-10">
                    {/* Gallery */}
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="aspect-[16/10] rounded-2xl overflow-hidden bg-surface border border-line"
                        >
                            <img
                                src={images[active]}
                                alt={`${car.brand} ${car.model}`}
                                className="w-full h-full object-cover"
                                data-testid="car-detail-image"
                            />
                        </motion.div>
                        {images.length > 1 && (
                            <div className="mt-4 grid grid-cols-4 gap-3">
                                {images.map((src, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActive(i)}
                                        className={`aspect-[4/3] rounded-xl overflow-hidden border transition-all ${
                                            active === i ? "border-gold" : "border-line hover:border-line-strong"
                                        }`}
                                        data-testid={`car-thumb-${i}`}
                                    >
                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="lg:col-span-5">
                        <span className="text-[10px] uppercase tracking-[0.32em] text-gold">
                            {car.brand}
                        </span>
                        <h1 className="font-display text-4xl lg:text-5xl tracking-tightest mt-3" data-testid="car-detail-title">
                            {car.brand} {car.model}
                        </h1>
                        <div className="mt-6 font-display text-4xl text-gradient-gold" data-testid="car-detail-price">
                            {FORMAT.money(car.price)}
                        </div>

                        <div className="mt-8 grid grid-cols-2 gap-4">
                            {specs.map(({ icon: Icon, label, value }) => (
                                <div
                                    key={label}
                                    className="p-4 bg-surface border border-line rounded-xl"
                                >
                                    <Icon size={14} className="text-gold mb-2" />
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                                        {label}
                                    </div>
                                    <div className="text-ink/90 text-sm mt-1">{value}</div>
                                </div>
                            ))}
                        </div>

                        {car.description && (
                            <div className="mt-8 p-6 bg-surface border border-line rounded-2xl">
                                <h3 className="font-display text-xl mb-3">Описание</h3>
                                <p className="text-ink-muted leading-relaxed text-sm">
                                    {car.description}
                                </p>
                            </div>
                        )}

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a
                                href={`tel:${SITE.phoneRaw}`}
                                className="flex-1 inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-bg font-medium px-6 py-4 rounded-full transition-all"
                                data-testid="car-detail-call"
                            >
                                <Phone size={16} /> Обади се
                            </a>
                            <a
                                href={`mailto:${SITE.email}?subject=Запитване за ${car.brand} ${car.model}`}
                                className="flex-1 inline-flex items-center justify-center gap-3 border border-gold/60 hover:bg-gold/10 text-gold px-6 py-4 rounded-full transition-all"
                                data-testid="car-detail-email"
                            >
                                <Mail size={16} /> Имейл
                            </a>
                        </div>

                        {car.source_url && (
                            <a
                                href={car.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-4 w-full inline-flex items-center justify-center gap-3 border border-line hover:border-gold/60 hover:bg-gold/5 text-ink-muted hover:text-gold px-6 py-4 rounded-full transition-all text-sm"
                                data-testid="car-detail-mobile-bg-link"
                            >
                                <ExternalLink size={16} /> Виж обявата в mobile.bg
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
